import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Form } from "react-bootstrap";
import { PriceHelper } from "../../../helpers";
import FormToastComponent from "../../basics/FormToastComponent";
import EventOption from "../../../models/event/EventOption";

/**
 * Select options of an event (console registration create / edit).
 * Value format: [{ option: id, quantity: n }]
 */
class OptionSelectComponent extends React.Component {
    static selection = (value) => (value || []).map(item => ({ option: item.option, quantity: item.quantity || 1 }));

    /**
     * Default options (active), one per group with one choice
     */
    static defaults = (groups) => {
        let selection = [];

        groups.forEach(group => {
            let defaults = group.activeOptions().filter(option => option.is_default);
            if (group.isSingle()) defaults = defaults.slice(0, 1);

            defaults.forEach(option => selection.push({ option: option.id, quantity: 1 }));
        });

        return selection;
    }

    static calculatePrice = (groups, value, period) => {
        let price = 0;

        OptionSelectComponent.selection(value).forEach(item => {
            groups.forEach(group => group.options.forEach(option => {
                if (option.id === item.option) price += option.calculatePrice(period, item.quantity);
            }));
        });

        return Math.round(price * 100) / 100;
    }

    /**
     * Only keep options that belong to the given groups
     */
    static filter = (groups, value) => {
        let ids = [];
        groups.forEach(group => group.options.forEach(option => ids.push(option.id)));

        return OptionSelectComponent.selection(value).filter(item => ids.indexOf(item.option) > -1);
    }

    static validate = (groups, value) => {
        let selection = OptionSelectComponent.selection(value);

        let valid = groups.every(group => !group.required || group.options.some(option => selection.some(item => item.option === option.id)));
        if (!valid) FormToastComponent.errorTrans('CMS.Event.option.error.event_option_group_required');

        return valid;
    }

    /**
     * Show a translated toast for option errors returned by the api
     */
    static handleError = (error) => {
        let message = error?.response?.data?.message;
        if (typeof message === 'string' && message.indexOf('event_option') === 0) {
            FormToastComponent.errorTrans('CMS.Event.option.error.' + message);
            return true;
        }

        return false;
    }

    selection = () => OptionSelectComponent.selection(this.props.value);

    quantity = (option) => (this.selection().filter(item => item.option === option.id)[0] || {}).quantity || 0;

    setQuantity = (group, option, quantity) => {
        let selection = this.selection().filter(item => item.option !== option.id);

        if (group.isSingle()) {
            let groupOptionIds = group.options.map(o => o.id);
            selection = selection.filter(item => groupOptionIds.indexOf(item.option) === -1);
        }

        if (quantity > 0) selection.push({ option: option.id, quantity: quantity });

        this.props.onChange(selection);
    }

    visibleOptions = (group) => group.options.filter(option => option.active || this.quantity(option) > 0);

    optionLabel = (option) => (
        <>
            {option.title} (&euro; {PriceHelper.price(option.priceValue())}
            {option.price_type === EventOption.PRICE_TYPE_PER_DAY && <> / <FormattedMessage id="CMS.Event.option.select.per_day" /></>})
        </>
    )

    render() {
        const groups = this.props.groups.filter(group => this.visibleOptions(group).length > 0);
        if (groups.length === 0) return null;

        const optionsPrice = this.props.free ? 0 : OptionSelectComponent.calculatePrice(this.props.groups, this.props.value, this.props.period);

        return (
            <div className="event-option-select">
                <FormattedMessage id="CMS.Event.option.select.title">
                    {value => <Form.Label className="event-option-select-title">{value}</Form.Label>}
                </FormattedMessage>

                {groups.map(group => (
                    <Form.Group key={group.id} className="event-option-select-group">
                        <p className="event-option-select-group-title">
                            {group.title}{group.required && ' *'}
                        </p>
                        {group.description && <p className="input-info">{group.description}</p>}

                        {group.isSingle() && !group.required && (
                            <FormattedMessage id="CMS.Event.option.select.none">
                                {value => (
                                    <Form.Check type="radio" id={'option-none-' + group.id} name={'option-group-' + group.id} label={value}
                                        checked={this.visibleOptions(group).every(option => this.quantity(option) === 0)}
                                        onChange={() => this.visibleOptions(group).forEach(option => this.quantity(option) && this.setQuantity(group, option, 0))} />
                                )}
                            </FormattedMessage>
                        )}

                        {this.visibleOptions(group).map(option => (
                            <div key={option.id} className="event-option-select-option">
                                <Form.Check type={group.isSingle() ? 'radio' : 'checkbox'} id={'option-' + option.id} name={'option-group-' + group.id}
                                    label={this.optionLabel(option)}
                                    checked={this.quantity(option) > 0}
                                    onChange={() => this.setQuantity(group, option, group.isSingle() ? 1 : (this.quantity(option) > 0 ? 0 : 1))} />

                                {!group.isSingle() && option.max_quantity > 1 && this.quantity(option) > 0 && (
                                    <Form.Control type="number" size="sm" min={1} max={option.max_quantity} value={this.quantity(option)}
                                        onChange={e => this.setQuantity(group, option, Math.min(option.max_quantity, Math.max(1, parseInt(e.target.value) || 1)))} />
                                )}
                            </div>
                        ))}
                    </Form.Group>
                ))}

                <p className="event-option-select-total">
                    <FormattedMessage id="CMS.Event.option.select.options_price" values={{ price: PriceHelper.price(optionsPrice) }} />
                    {this.props.basePrice !== null && (
                        <>
                            <br />
                            <strong><FormattedMessage id="CMS.Event.option.select.total_price" values={{ price: PriceHelper.price(this.props.free ? 0 : this.props.basePrice + optionsPrice) }} /></strong>
                        </>
                    )}
                </p>
            </div>
        );
    }
}

OptionSelectComponent.propTypes = {
    groups: PropTypes.array.isRequired,
    value: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    period: PropTypes.object,
    free: PropTypes.bool,
    basePrice: PropTypes.number
};

OptionSelectComponent.defaultProps = {
    value: [],
    period: null,
    free: false,
    basePrice: null
};

export default OptionSelectComponent;
